/**
 * kennisbank.js — de interface van de kennisbank in NL en EN: /kennisbank,
 * /kennisbank/:slug, /kennisbank/auteur/:slug en de Engelse tegenhangers onder
 * /en/knowledge-base. De artikelen zelf (src/data/blogArticles.js) blijven
 * Nederlands; in het Engels staat er het label "Article in Dutch" bij.
 * Vorm van NL en EN moet gelijk zijn (paginas.test.js).
 *
 * Teksten met een getal of naam zijn opgeknipt (voor/na), zodat de JSX het
 * getal of de naam zelf invult.
 */

const NL = {
  datumLocale: 'nl-NL',
  kruimelHome: 'Home',
  kruimelKennisbank: 'Kennisbank',
  // Thema-namen per id uit THEMAS in src/utils/kennisbank.js.
  themas: {
    'toetsen-maken': 'Toetsen maken',
    'ai-en-data': 'AI en data',
    privacy: 'Privacy en veiligheid',
    'in-de-klas': 'In de klas',
    starten: 'Starten en kiezen',
  },
  artikelInNl: 'Artikel in het Nederlands',
  artikel: { een: '1 artikel', meer: 'artikelen' },

  overzicht: {
    seoTitel: 'Kennisbank - TestWisely | Artikelen over Digitaal Toetsen',
    seoBeschrijving: 'Lees onze artikelen over digitaal toetsen, AI nakijken, formatief toetsen en meer. Praktische tips en inzichten voor docenten in het voortgezet onderwijs.',
    seoKeywords: 'digitaal toetsen artikelen, toetsplatform blog, AI nakijken tips, formatief toetsen, onderwijs technologie',
    kop: { voor: 'Kennis', accent: 'bank' },
    intro: 'Praktische artikelen over digitaal toetsen, AI-nakijken en privacy. Voor docenten en scholen in het voortgezet onderwijs.',
    zoekPlaceholder: 'Zoek een onderwerp, bijv. AVG',
    zoekLabel: 'Zoek in de kennisbank',
    themaLabel: 'Kies een thema',
    alles: 'Alles',
    geenArtikel: 'Geen artikel gevonden',
    geenArtikelVoor: ' voor ',
    toonAlles: 'Toon alle artikelen',
    ctaKop: { voor: 'Klaar om digitaal te ', accent: 'toetsen', na: '?' },
    ctaTekst: 'Ontdek zelf hoe TestWisely uw toetsproces transformeert.',
    ctaDemo: 'DEMO AANVRAGEN',
    ctaHoe: 'HOE WERKT HET?',
  },

  artikelPagina: {
    titelAchter: ' | TestWisely Kennisbank',
    bijgewerktOp: 'Bijgewerkt op ',
    leestijd: ' leestijd',
    inDitArtikel: 'In dit artikel',
    leesOok: 'Lees ook:',
    download: 'Download',
    leesVerder: 'Lees verder',
    ctaKop: 'Zien hoe TestWisely werkt?',
    ctaTekst: 'Ontdek zelf hoe TestWisely uw toetsproces transformeert.',
    ctaDemo: 'DEMO AANVRAGEN',
  },

  auteur: {
    titelAchter: ' | TestWisely Kennisbank',
    beschrijvingVoor: 'Artikelen van ',
    beschrijvingNa: ' in de kennisbank van TestWisely. ',
    opLinkedIn: ' op LinkedIn',
    artikelenVan: 'Artikelen van ',
    nogGeen: { voor: 'Nog geen artikelen van ', na: '.' },
    terug: 'Alle artikelen in de kennisbank',
  },
};

const EN = {
  datumLocale: 'en-GB',
  kruimelHome: 'Home',
  kruimelKennisbank: 'Knowledge Base',
  themas: {
    'toetsen-maken': 'Creating tests',
    'ai-en-data': 'AI and data',
    privacy: 'Privacy and security',
    'in-de-klas': 'In the classroom',
    starten: 'Getting started and choosing',
  },
  artikelInNl: 'Article in Dutch',
  artikel: { een: '1 article', meer: 'articles' },

  overzicht: {
    seoTitel: 'Knowledge Base - TestWisely | Articles on Digital Assessment',
    seoBeschrijving: 'Articles on digital assessment, AI marking, formative assessment and more: practical tips for teachers in Dutch secondary education. The articles are written in Dutch.',
    seoKeywords: 'digital assessment articles, assessment platform blog, AI marking tips, formative assessment, education technology',
    kop: { voor: 'Knowledge ', accent: 'Base' },
    intro: 'Practical articles on digital assessment, AI marking and privacy, for teachers and schools in secondary education. The articles are written in Dutch.',
    zoekPlaceholder: 'Search a topic, e.g. AVG (GDPR)',
    zoekLabel: 'Search the knowledge base',
    themaLabel: 'Choose a topic',
    alles: 'All',
    geenArtikel: 'No article found',
    geenArtikelVoor: ' for ',
    toonAlles: 'Show all articles',
    ctaKop: { voor: 'Ready for digital ', accent: 'assessment', na: '?' },
    ctaTekst: 'Discover for yourself how TestWisely transforms your assessment process.',
    ctaDemo: 'REQUEST A DEMO',
    ctaHoe: 'HOW DOES IT WORK?',
  },

  artikelPagina: {
    titelAchter: ' | TestWisely Knowledge Base',
    bijgewerktOp: 'Updated on ',
    leestijd: ' read',
    inDitArtikel: 'In this article',
    leesOok: 'See also:',
    download: 'Download',
    leesVerder: 'Read more',
    ctaKop: 'See how TestWisely works?',
    ctaTekst: 'Discover for yourself how TestWisely transforms your assessment process.',
    ctaDemo: 'REQUEST A DEMO',
  },

  auteur: {
    titelAchter: ' | TestWisely Knowledge Base',
    beschrijvingVoor: 'Articles by ',
    beschrijvingNa: ' in the TestWisely knowledge base. ',
    opLinkedIn: ' on LinkedIn',
    artikelenVan: 'Articles by ',
    nogGeen: { voor: 'No articles by ', na: ' yet.' },
    terug: 'All articles in the knowledge base',
  },
};

/**
 * Engelse functie, rol en bio per auteur (slug uit src/data/authors.js).
 * De Nederlandse staan in authors.js zelf; kennisbank.engels.test.js eist dat
 * elke auteur met een slug hier een Engelse versie heeft.
 */
export const AUTEURS_EN = {
  'yasar-kocdas': {
    functie: 'Founder & CEO',
    role: 'Founder & CEO TestWisely',
    bio: 'Founder of TestWisely and former teacher. Building a platform that gives teachers more time for what really matters: teaching.',
  },
  'mohamed-bentaher': {
    functie: 'Lead Developer',
    role: 'Lead Developer TestWisely',
    bio: 'Lead Developer at TestWisely. Builds the platform itself: the test environment, the marking and everything teachers and students do with it.',
  },
  'ismail-marghich': {
    functie: 'Quality Assurance Engineer',
    role: 'Quality Assurance Engineer TestWisely',
    bio: 'Quality Assurance Engineer at TestWisely. Tests every new feature before it reaches schools, from secure testing to accessibility.',
  },
  'ilias-amejar': {
    functie: 'Legal Counsel',
    role: 'Legal Counsel TestWisely',
    bio: 'Legal Counsel at TestWisely. Works on privacy, the GDPR and the legal side of digital assessment in education.',
  },
};

/** De auteur in de taal van de pagina: in EN functie, rol en bio uit AUTEURS_EN. */
export function auteurInTaal(author, lang) {
  if (lang !== 'en' || !author) return author;
  const en = author.slug ? AUTEURS_EN[author.slug] : null;
  if (en) return { ...author, ...en };
  // Redactie zonder pagina (FALLBACK in authors.js).
  return { ...author, role: 'TestWisely editorial team', bio: 'Written by the TestWisely team.' };
}

const KENNISBANK_TEKSTEN = { nl: NL, en: EN };

export function kennisbankTeksten(lang = 'nl') {
  return KENNISBANK_TEKSTEN[lang] || NL;
}

export default KENNISBANK_TEKSTEN;
